import { useMemo } from "react";

import {
  getGroupIdFromAccessId,
  getIdFromAccessId,
  getStudentIdFromAccessId,
  groupAccessIdFromGroupId,
  isGroupAccessId,
  isStudentAccessId,
  studentAccessIdFromUserId,
} from "../../../shared/access-id";
import { useIsAssessing } from "../section/assessment/hooks";

import { indexToLetters } from "./helpers";

import type {
  PageGroupType,
  SectionUserType,
  SubmissionType,
  UserType,
} from "components/server-types";
import { InteractionMode } from "components/server-types";
import { t } from "i18n/i18n";
import { ORDER_BY_TITLE } from "mds/hooks/use-ordered";
import { useWorksheetAccessIds } from "sharedb/ShareDBDoc";
import { useCapabilities } from "store/capability-hooks";
import { store } from "store/index";
import {
  pageGroupSelectors,
  pageSelectors,
  selectCurrentSection,
  selectSortedSubmissionsByPageId,
  selectUsersWhoCanParticipateInSection,
} from "store/selectors";
import { selectPageGroupUsersByPageId } from "store/selectors/page-groups";
import type { ExtendedPageGroupUserType } from "store/selectors/types";
import { useListSelector, useObjectSelector } from "store/store-hooks";
import { filterMap } from "utils/collections";
import { getUserShortName } from "utils/user-utils";

type AssessmentSortSubmission = Pick<
  SubmissionType,
  "official_at" | "section_user_id" | "page_group_id"
>;
type SectionUsersById = Record<string, Pick<SectionUserType, "user_id"> | undefined>;

export type ResponseType = {
  title: string;
  id: string;
};

/** Map access ids to their display order in the Class Responses dropdown. */
export const getResponseOrderByAccessId = (responses: ResponseType[]) =>
  new Map(
    filterMap(
      responses,
      (response) => Boolean(response.id),
      (response, index) => [response.id, index],
    ),
  );

export const compareAccessIdsByResponseOrder =
  (responseOrderByAccessId: Map<string, number>) => (a: string, b: string) => {
    const aIndex = responseOrderByAccessId.get(a);
    const bIndex = responseOrderByAccessId.get(b);
    if (aIndex !== undefined && bIndex !== undefined) {
      return aIndex - bIndex;
    }
    if (aIndex !== undefined) {
      return -1;
    }
    if (bIndex !== undefined) {
      return 1;
    }
    return a.localeCompare(b);
  };

/**
 * Anonymize responses by shuffling them with a deterministic seed (pageId).
 * Using Fisher-Yates shuffle algorithm.
 */

export const anonymizeResponses = (
  pageId: string,
  responses: ResponseType[],
  interactionMode: InteractionMode,
): ResponseType[] => {
  const seededRandom = (seed: number) => {
    // Deterministic pseudo-random number generator
    const x = Math.sin(seed) * 10000;
    // Number returned is between 0 and 1
    return x - Math.floor(x);
  };
  // Create a seed by summing the char codes of the pageId
  let pageSeed = pageId.split("").reduce((acc, char) => acc + char.charCodeAt(0), 0);

  // We pre-sort by ID in case the responses aren't. Sometimes "order" is "0" for class responses
  // and we want to ensure that the order is consistent going into the anonymization function.
  const shuffledResponses = [...responses].sort((a, b) => a.id.localeCompare(b.id));
  for (let i = shuffledResponses.length - 1; i > 0; i -= 1) {
    // Generate a pseudo-random index based on the seed
    const j = Math.floor(seededRandom(pageSeed) * (i + 1));
    // Swap elements at index i and j
    [shuffledResponses[i], shuffledResponses[j]] = [shuffledResponses[j], shuffledResponses[i]];
    pageSeed += 1; // Increment the seed to change the random number sequence
  }

  if (interactionMode === InteractionMode.INDIVIDUAL) {
    //   Rename to Student 1, Student 2, etc.
    return shuffledResponses.map((response, index) => ({
      title: t("common.student_title", { number: index + 1 }),
      id: response.id,
    }));
  }
  // Rename to Anon. Group 1, Anon. Group 2, etc.
  return shuffledResponses.map((response, index) => ({
    title: t("common.group_title_anonymous", { letter: indexToLetters(index) }),
    id: response.id,
  }));
};

// Note that that for groups pages, we handle differently depending on assessment mode:
// Outside of assessment, we show all the groups (even empty ones).
// This is because the instructor can see all groups in the group configuration tab and if group membership has been changed,
// the empty groups can contain the old student data.
// However, when assessing, we only want to show the groups that have submissions, because
// there is no purpose to assess any work not tied to a student
// (and it's likely a blank submission from an empty group in standard use, given we
// commonly have 1-2 extra groups for students self-assign groups).
export const getResponses = (
  anonymize: boolean,
  isAssessing: boolean,
  pageId: string | null,
  interactionMode: InteractionMode | null,
  isImported: boolean,
  pageGroups: Pick<PageGroupType, "id">[],
  studentUsers: UserType[],
  worksheetAccessIds: string[],
  enabledPageGroupUsers?: ExtendedPageGroupUserType[],
): ResponseType[] => {
  if (!pageId || !interactionMode) {
    return [];
  }
  // Class pages don't feature a responses tab
  if (interactionMode === InteractionMode.SHARED_CLASS) {
    return [];
  }

  let responses = [] as ResponseType[];

  if (interactionMode === InteractionMode.INDIVIDUAL) {
    // Start by populating the options for all known participating students.
    const knownUserResponses = studentUsers.map((user) => ({
      title: getUserShortName(user),
      id: studentAccessIdFromUserId(user.id),
    }));
    responses = responses.concat(knownUserResponses);

    // Potentially extend that set of options with additional users gleaned from the worksheet. This takes care
    // of the case of a Page created by means of uploading a JSON snapshot, which will tend to have a worksheet
    // that makes reference to Users that don't actually exist in the current environment.
    if (isImported && worksheetAccessIds.length > 0) {
      const knownUserIds = new Set(studentUsers.map((user) => user.id));
      const accessIdsFromWorksheet = worksheetAccessIds.filter(isStudentAccessId);
      const userIdsFromWorksheet = accessIdsFromWorksheet.map(getStudentIdFromAccessId);
      const unknownUserIds = userIdsFromWorksheet.filter((id) => !knownUserIds.has(id));
      const unknownUserResponses = unknownUserIds.map((userId) => ({
        title: `User ${userId}`,
        id: studentAccessIdFromUserId(userId),
      }));
      responses = responses.concat(unknownUserResponses);
    }
  } else {
    // TODO: this logic somewhat duplicates the logic in getTotalSubmissions in assessment/helpers.ts
    // Extract into a helper function.
    // Note that unlike `getTotalSubmissions`, when not assessing
    // we want to show all groups (even empty ones).
    const visibleGroupIds =
      isAssessing && enabledPageGroupUsers
        ? new Set(enabledPageGroupUsers.map((pgu) => pgu.page_group_id))
        : new Set(pageGroups.map((pg) => pg.id)); // Show all groups when not assessing

    // Start by populating the options for visibleGroupIds according to PageGroup data.
    // Preserve original group numbers by using the original index from pageGroups.
    const knownGroupResponses = filterMap(
      pageGroups,
      (group) => visibleGroupIds.has(group.id),
      (group, originalIndex) => ({
        title: t("common.group_title", { number: originalIndex + 1 }),
        id: groupAccessIdFromGroupId(group.id),
      }),
    );
    responses = responses.concat(knownGroupResponses);

    // Potentially extend options with additional groups gleaned from the worksheet, similar to above.
    if (isImported && worksheetAccessIds.length > 0) {
      const knownGroupIds = new Set(pageGroups.map((pageGroup) => pageGroup.id));
      const accessIdsFromWorksheet = worksheetAccessIds.filter(isGroupAccessId);
      const groupIdsFromWorksheet = accessIdsFromWorksheet.map(getGroupIdFromAccessId);
      const unknownGroupIds = groupIdsFromWorksheet.filter((id) => !knownGroupIds.has(id));
      const unknownGroupResponses = unknownGroupIds.map((groupId) => ({
        title: `Group ${groupId}`,
        id: groupAccessIdFromGroupId(groupId),
      }));
      responses = responses.concat(unknownGroupResponses);
    }
  }

  // Optional anonymization.
  if (anonymize && pageId) {
    responses = anonymizeResponses(pageId, responses, interactionMode);
  } else {
    // Ensure consistent ordering of responses when not anonymizing.
    // Use natural sort so "Group 1", "Group 2", "Group 10" order numerically, not alphabetically.
    responses = [...responses].sort(ORDER_BY_TITLE);
  }

  // Offer the default "class responses" option first, unless we're in the middle of an assessment.
  // Do this after anonymization, so that the "class responses" option is always the first one.
  if (!isAssessing) {
    responses = [
      {
        title: t("presentation.view_options.responses"),
        id: "",
      },
      ...responses,
    ];
  }

  return responses;
};

const filterSubmissionsByResponse = (
  submission: AssessmentSortSubmission,
  response: ResponseType,
  sectionUsersById: SectionUsersById,
) => {
  const id = getIdFromAccessId(response.id);
  if (!id) return false;

  if (submission.section_user_id !== null) {
    return sectionUsersById[submission.section_user_id]?.user_id === id;
  }
  return submission.page_group_id === id;
};

/**
 * This function sorts an array of responses based on the following logic:
 * - Responses whose most recent submission is assessed come first.
 * - Responses with submissions (but none assessed) come second.
 * - Responses with assessed submissions come third.
 * - Responses with no submissions come last.
 * - Among responses with assessed submissions, non-official submissions come before official ones.
 *
 * Expects `submissions` newest-first by `created_at` (e.g. from `selectSortedSubmissionsByPageId`).
 */
export const compareResponsesForAssessmentMode =
  (submissions: AssessmentSortSubmission[], sectionUsersById: SectionUsersById) =>
  (a: ResponseType, b: ResponseType) => {
    // Find the group or student submission based on the access id in the first response.
    const aSubmissions = submissions.filter((submission) =>
      filterSubmissionsByResponse(submission, a, sectionUsersById),
    );

    // Find the group or student submission based on the access id in the second response.
    const bSubmissions = submissions.filter((submission) =>
      filterSubmissionsByResponse(submission, b, sectionUsersById),
    );

    // Helper function to determine the tier for a set of submissions
    const getTier = (submissionList: AssessmentSortSubmission[]) => {
      // No submissions
      if (submissionList.length === 0) {
        return 4;
      }

      // Last submission is the official submission we want first
      if (submissionList[0].official_at) {
        return 1;
      }

      // Some assessed submissions
      const hasAssessed = submissionList.some((submission) => submission.official_at);
      if (hasAssessed) return 3;

      // Else, some unassessed submissions
      return 2;
    };

    const aTier = getTier(aSubmissions);
    const bTier = getTier(bSubmissions);

    // Sort by tier (lower tier comes first)
    return aTier - bTier;
  };

export const useResponsesForPage = (pageId: string | null, isFeaturing = false) => {
  const page = useObjectSelector(
    (s) => pageSelectors.selectById(s, pageId),
    [
      "anonymize_student_responses",
      "assessments_published_at",
      "interaction_mode",
      "is_imported",
      "worksheet_id",
    ],
  );
  const assessmentsPublished = !!page?.assessments_published_at;
  const currentSection = useObjectSelector(selectCurrentSection, ["anonymous_grading_enabled"]);
  const isAssessing = useIsAssessing();
  const submissions = useListSelector(
    (s) => selectSortedSubmissionsByPageId(s, pageId),
    ["created_at", "official_at", "section_user_id", "page_group_id"],
  );
  const { canParticipateInSection } = useCapabilities();
  // TODO: add value here to allow instructor to anonymize a course
  const anonymize =
    (isAssessing && !assessmentsPublished && currentSection?.anonymous_grading_enabled) ||
    (page?.anonymize_student_responses && isFeaturing) ||
    (canParticipateInSection && !isFeaturing);
  const studentUsers = useListSelector(selectUsersWhoCanParticipateInSection);
  // Ids only; order is preserved for "Group N" labeling via originalIndex.
  const pageGroups = useListSelector((s) => pageGroupSelectors.filterByPageId(s, pageId), ["id"]);
  // Only fetch pageGroupUsers for group pages (not needed for individual pages)
  const pageGroupUsers = useListSelector((s) => selectPageGroupUsersByPageId(s, pageId));

  const worksheetAccessIds = useWorksheetAccessIds(page?.worksheet_id);
  const responses = useMemo(
    () =>
      getResponses(
        anonymize,
        isAssessing,
        pageId,
        page?.interaction_mode ?? null,
        page?.is_imported ?? false,
        pageGroups,
        studentUsers,
        worksheetAccessIds,
        pageGroupUsers,
      ),
    [
      anonymize,
      isAssessing,
      pageId,
      page?.interaction_mode,
      page?.is_imported,
      pageGroups,
      studentUsers,
      worksheetAccessIds,
      pageGroupUsers,
    ],
  );

  // When loading the page while in assessment mode, we need to wait for submissions
  // to be loaded before we do the initial sort, otherwise we get stuck in weirdly ordered state.
  const submissionsLoaded = submissions.length > 0;

  const sortedResponses = useMemo(() => {
    if (isAssessing) {
      // Snapshot section_users with submissions — intentional: do not re-sort when either changes.
      const sectionUsersById = store.getState().section_users;
      return responses.toSorted(compareResponsesForAssessmentMode(submissions, sectionUsersById));
    }

    return responses;
    // Do not resort when submissions change. We do not want the order of responses
    // to change when a new assessment is created while in assessment mode.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAssessing, submissionsLoaded, anonymize]);

  // If we are not assessing, we don't want the cached version otherwise with anonymize toggle
  // will not work properly.
  return { responses: isAssessing ? sortedResponses : responses, anonymize };
};
